import { isLiveSubscriptionStatus } from "@intelligems/ig-types/dtos/organization-subscriptions";

import { orderVolumeTier, type OrderVolumeTier } from "@src/constants/billing";

import type {
  BillingAccountState,
  BillingGateModel,
  BillingGateModuleViewModel,
  BillingGateUserState,
} from "../types";
import {
  MODULE_ORDER,
  SMART_PRICING_VARIANTS,
  UNLIMITED_ID,
} from "./constants";
import { getBillingGatePresentation } from "./gatePresentation";
import type { BillingCatalog } from "./types";

type ActiveSubscription = Readonly<{
  status?: string | null;
  items?: readonly Readonly<{ trialing: boolean }>[];
}>;

/**
 * True only when the org has a subscription that is actually producing
 * revenue.
 *
 * `subscriptions.active` holds every *live* subscription — active, trialing,
 * and past_due — so status alone cannot separate a paying customer from a
 * trial. Two shapes of trial hide in that list:
 *
 *   - a subscription-level trial, which reads `status: "trialing"`;
 *   - a per-module trial (ADR 0013), which leaves the subscription `active`
 *     while every item bills $0 against its attached Trial Offer.
 *
 * Both are prospects: they have paid nothing and are leads rather than
 * upgrades. A net-new install has no subscription row at all and falls out
 * here too.
 *
 * A legacy subscription counts regardless of trial: the legacy table stores
 * trialing Stripe subscriptions as `active` and keeps no trial state, so the
 * flag cannot tell a legacy trial from a paying legacy org.
 *
 * `items` is absent on projections written before the cache carried it;
 * absent is treated as billing so a real customer is never demoted to a
 * prospect by stale data.
 */
export const isExistingCustomer = (
  subscriptions: Readonly<{
    active: readonly ActiveSubscription[];
    hasActiveLegacySubscription?: boolean;
  }>
): boolean =>
  subscriptions.hasActiveLegacySubscription === true ||
  subscriptions.active.some(
    ({ status, items }) =>
      isLiveSubscriptionStatus(status) &&
      status !== "trialing" &&
      (items === undefined || items.some(({ trialing }) => !trialing))
  );

/**
 * Whether an Unlimited plan at this order volume actually comes with a
 * dedicated Customer Success manager. Only the high tier is staffed, so every
 * surface that advertises expert support asks here rather than re-deriving it.
 */
export const hasExpertSupport = (tier: OrderVolumeTier): boolean =>
  tier === "high";

type BillingGateInput = Readonly<{
  accountState: BillingAccountState;
  activeSubscriptions: readonly ActiveSubscription[];
  hasActiveLegacySubscription?: boolean;
  catalog: BillingCatalog;
  ordersPerMonth: number;
  isModular: boolean;
}>;

const userStateFor = (
  accountState: BillingAccountState
): BillingGateUserState => {
  switch (accountState) {
    case "pre_trial":
    case "trialing":
      return "trial";
    case "active":
    case "past_due":
    case "paused":
      return "paid_other";
    case "expired":
      return "expired";
  }
};

const isCurrentUnlimited = (catalog: BillingCatalog): boolean =>
  catalog.currentPlan?.id === UNLIMITED_ID ||
  catalog.plans.some(({ id, isCurrent }) => id === UNLIMITED_ID && isCurrent);

const isActiveModule = (
  moduleId: string,
  activeModuleIds: ReadonlySet<string>
): boolean =>
  activeModuleIds.has(moduleId) ||
  (SMART_PRICING_VARIANTS.has(moduleId) &&
    [...SMART_PRICING_VARIANTS].some((id) => activeModuleIds.has(id)));

// Per-module decision math shared by the full gate model and scenario
// previews that supply the bypass flags explicitly instead of via a catalog.
export const projectModuleGateDecision = ({
  accountState,
  activeSubscriptions,
  hasActiveLegacySubscription,
  isModuleActive,
  isUnlimited,
  moduleId,
  ordersPerMonth,
}: Readonly<{
  accountState: BillingAccountState;
  activeSubscriptions: readonly ActiveSubscription[];
  hasActiveLegacySubscription?: boolean;
  isModuleActive: boolean;
  isUnlimited: boolean;
  moduleId: string;
  ordersPerMonth: number;
}>): BillingGateModuleViewModel => {
  const userState = userStateFor(accountState);
  const presentation = getBillingGatePresentation(moduleId);
  // Carried on bypass decisions too: an org can clear a module gate on a
  // trial's entitlements, and the Smart Pricing specialist CTA behind the
  // bypass still has to route trials to Sales rather than the CS calendar.
  const segment = isExistingCustomer({
    active: activeSubscriptions,
    hasActiveLegacySubscription,
  })
    ? "existing_customer"
    : "prospect";

  if (isUnlimited || moduleId === UNLIMITED_ID) {
    return {
      ...presentation,
      kind: "bypass",
      moduleId,
      reason: "unlimited",
      segment,
      userState,
    };
  }
  if (isModuleActive) {
    return {
      ...presentation,
      kind: "bypass",
      moduleId,
      reason: "module_active",
      segment,
      userState,
    };
  }

  return {
    ...presentation,
    kind: "gate",
    moduleId,
    segment,
    tier: orderVolumeTier(ordersPerMonth),
    userState,
  };
};

// Admins bypass every module gate. Built without catalog input so admins stay
// unblocked even while the billing snapshot is loading or errored — some
// consumers (e.g. the create-experience dialog) fail closed on non-ready
// status or a missing decision.
export const projectAdminBypassGateModel = (): BillingGateModel => {
  const byModuleId: Record<string, BillingGateModuleViewModel> = {};

  for (const moduleId of new Set([
    ...MODULE_ORDER,
    ...SMART_PRICING_VARIANTS,
    UNLIMITED_ID,
  ])) {
    byModuleId[moduleId] = {
      ...getBillingGatePresentation(moduleId),
      kind: "bypass",
      moduleId,
      reason: "admin",
      // Admins are internal staff, not leads; keep their specialist CTAs on
      // the consult calendar rather than routing them to Sales.
      segment: "existing_customer",
      userState: "paid_other",
    };
  }

  return { byModuleId, status: "ready" };
};

export const projectBillingGateModel = ({
  accountState,
  activeSubscriptions,
  hasActiveLegacySubscription,
  catalog,
  ordersPerMonth,
  isModular,
}: BillingGateInput): BillingGateModel => {
  const activeModuleIds = new Set(catalog.activeModules.map(({ id }) => id));
  const unlimited = isCurrentUnlimited(catalog);
  const moduleIds = new Set([
    ...MODULE_ORDER,
    ...catalog.modules.map(({ id }) => id),
    ...catalog.smartPricingVariants.map(({ id }) => id),
    UNLIMITED_ID,
  ]);
  const byModuleId: Record<string, BillingGateModuleViewModel> = {};

  for (const moduleId of moduleIds) {
    if (!isModular) {
      byModuleId[moduleId] = {
        ...getBillingGatePresentation(moduleId),
        kind: "bypass",
        moduleId,
        reason: "legacy_billing",
        segment: isExistingCustomer({
          active: activeSubscriptions,
          hasActiveLegacySubscription,
        })
          ? "existing_customer"
          : "prospect",
        userState: userStateFor(accountState),
      };
      continue;
    }

    byModuleId[moduleId] = projectModuleGateDecision({
      accountState,
      activeSubscriptions,
      hasActiveLegacySubscription,
      isModuleActive: isActiveModule(moduleId, activeModuleIds),
      isUnlimited: unlimited,
      moduleId,
      ordersPerMonth,
    });
  }

  return { byModuleId, status: "ready" };
};
